/**
 * Frontend Money Utilities
 * 
 * This module provides functions for displaying and handling monetary values
 * in the user interface. Works with backend Decimal128 values sent as strings.
 * 
 * All money values from API are expected as strings with 2 decimal places (e.g., "10.50")
 */

// ============================================
// NORMALIZATION HELPERS
// ============================================

export const toNumberSafe = (value, fallback = 0) => {
  if (value === null || value === undefined) {
    return fallback;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? fallback : value;
  }

  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  if (typeof value === 'object') {
    if (typeof value.$numberDecimal === 'string') {
      const parsed = parseFloat(value.$numberDecimal);
      return Number.isNaN(parsed) ? fallback : parsed;
    }

    if (value._bsontype === 'Decimal128' && typeof value.toString === 'function') {
      const parsed = parseFloat(value.toString());
      return Number.isNaN(parsed) ? fallback : parsed;
    }
  }

  return fallback;
};

// ============================================
// DISPLAY FORMATTING FUNCTIONS
// ============================================

/**
 * Format money string to display currency
 * @param {string|number} value - Money value as string (e.g., "10.50") or number
 * @param {string} currency - Currency code (default: "MYR")
 * @param {Object} options - Additional formatting options
 * @returns {string} Formatted currency string (e.g., "RM 10.50")
 * @example
 * formatCurrency("10.50") // Returns "RM 10.50"
 * formatCurrency("1000.00", "USD") // Returns "US$1,000.00"
 * formatCurrency("10.50", "MYR", { minimumFractionDigits: 2 }) // Returns "RM 10.50"
 */
export const formatCurrency = (value, currency = 'MYR', options = {}) => {
  // Handle null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    value = 0;
  }

  // Convert value to number using toNumberSafe to handle Decimal128 objects
  const numValue = toNumberSafe(value, 0);

  // Handle NaN
  if (isNaN(numValue)) {
    console.warn('formatCurrency: Invalid value', value);
    return `RM 0.00`;
  }

  // Validate and sanitize currency code
  const validCurrency = (currency && typeof currency === 'string' && currency.length === 3)
    ? currency.toUpperCase()
    : 'MYR';

  // Default options for 2 decimal places
  const formatOptions = {
    style: 'currency',
    currency: validCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  };

  try {
    return new Intl.NumberFormat('en-MY', formatOptions).format(numValue);
  } catch (error) {
    console.error('formatCurrency error:', error, 'currency:', validCurrency, 'value:', numValue, 'formatOptions:', formatOptions);
    // Fallback to simple formatting
    const prefix = validCurrency === 'MYR' ? 'RM' : validCurrency;
    return `${prefix} ${numValue.toFixed(2)}`;
  }
};

/**
 * Format currency with compact notation for large numbers
 * @param {string|number} value - Money value
 * @param {string} currency - Currency code (default: "MYR")
 * @returns {string} Compact formatted string (e.g., "RM 1.5M")
 * @example
 * formatCompactCurrency("1500000") // Returns "RM 1.5M"
 * formatCompactCurrency("5000") // Returns "RM 5K"
 * formatCompactCurrency("500") // Returns "RM 500.00"
 */
export const formatCompactCurrency = (value, currency = 'MYR') => {
  // Validate and sanitize currency code
  const validCurrency = (currency && typeof currency === 'string' && currency.length === 3)
    ? currency.toUpperCase()
    : 'MYR';

  const prefix = validCurrency === 'MYR' ? 'RM' : validCurrency;

  // Handle null, undefined, or empty
  if (value === null || value === undefined || value === '') {
    return `${prefix} 0.00`;
  }

  // Convert to number using toNumberSafe to handle Decimal128 objects
  const amount = toNumberSafe(value, 0);

  if (isNaN(amount)) {
    return `${prefix} 0.00`;
  }

  // Format based on magnitude
  if (amount >= 1000000000) {
    // Billions
    return `${prefix} ${(amount / 1000000000).toFixed(1)}B`;
  } else if (amount >= 1000000) {
    // Millions
    return `${prefix} ${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    // Thousands
    return `${prefix} ${(amount / 1000).toFixed(1)}K`;
  } else {
    // Less than 1000
    return `${prefix} ${amount.toFixed(2)}`;
  }
};

/**
 * Format money value as plain number string with 2 decimals (no currency symbol)
 * @param {string|number} value - Money value
 * @returns {string} Formatted number string (e.g., "10.50")
 * @example
 * formatMoneyPlain("10.5") // Returns "10.50"
 * formatMoneyPlain(10.5) // Returns "10.50"
 */
export const formatMoneyPlain = (value) => {
  if (value === null || value === undefined || value === '') {
    return '0.00';
  }

  // Convert to number using toNumberSafe to handle Decimal128 objects
  const numValue = toNumberSafe(value, 0);

  if (isNaN(numValue)) {
    return '0.00';
  }

  return numValue.toFixed(2);
};

/**
 * Format number with thousand separators (no currency symbol)
 * @param {string|number} value - Money value
 * @returns {string} Formatted number with commas (e.g., "1,234.56")
 * @example
 * formatNumberWithCommas("1234.56") // Returns "1,234.56"
 */
export const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === '') {
    return '0.00';
  }

  // Convert to number using toNumberSafe to handle Decimal128 objects
  const numValue = toNumberSafe(value, 0);

  if (isNaN(numValue)) {
    return '0.00';
  }

  return new Intl.NumberFormat('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
};

// ============================================
// INPUT PARSING FUNCTIONS
// ============================================

/**
 * Parse user input to valid money string
 * Cleans input and ensures 2 decimal places
 * @param {string} input - User input from form field
 * @returns {string} Formatted money string with 2 decimals
 * @example
 * parseMoneyInput("10.5") // Returns "10.50"
 * parseMoneyInput("10.555") // Returns "10.56" (rounded)
 * parseMoneyInput("RM 1,000.00") // Returns "1000.00"
 * parseMoneyInput("") // Returns "0.00"
 */
export const parseMoneyInput = (input) => {
  if (!input || input === '') {
    return '0.00';
  }

  // Remove currency symbols, spaces, and commas
  const cleaned = input.toString()
    .replace(/[RM$€£¥,\s]/g, '')
    .trim();

  // Parse to number
  const numValue = parseFloat(cleaned);

  if (isNaN(numValue)) {
    return '0.00';
  }

  // Round to 2 decimals and format
  return (Math.round(numValue * 100) / 100).toFixed(2);
};

/**
 * Validate money input format (max 2 decimal places)
 * @param {string} value - Input value to validate
 * @returns {boolean} True if valid
 * @example
 * isValidMoneyInput("10.50") // Returns true
 * isValidMoneyInput("10.555") // Returns false
 * isValidMoneyInput("abc") // Returns false
 */
export const isValidMoneyInput = (value) => {
  if (!value || value === '') {
    return true; // Allow empty
  }

  // Remove commas for validation
  const cleaned = value.toString().replace(/,/g, '');

  // Check format: optional negative, digits, optional decimal with 1-2 places
  const regex = /^-?\d+(\.\d{1,2})?$/;
  return regex.test(cleaned);
};

/**
 * Format input value for display in input field (as user types)
 * Allows partial input while typing
 * @param {string} value - Current input value
 * @returns {string} Formatted value for display
 * @example
 * formatInputValue("10") // Returns "10"
 * formatInputValue("10.") // Returns "10."
 * formatInputValue("10.5") // Returns "10.5"
 */
export const formatInputValue = (value) => {
  if (!value || value === '') {
    return '';
  }

  // Remove non-numeric characters except decimal point
  const cleaned = value.toString().replace(/[^\d.]/g, '');

  // Prevent multiple decimal points
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }

  // Limit decimal places to 2
  if (parts.length === 2 && parts[1].length > 2) {
    return parts[0] + '.' + parts[1].substring(0, 2);
  }

  return cleaned;
};

/**
 * Sanitize money input on blur (when user leaves field)
 * @param {string} value - Input value
 * @returns {string} Sanitized value with 2 decimals
 * @example
 * sanitizeMoneyOnBlur("10") // Returns "10.00"
 * sanitizeMoneyOnBlur("10.5") // Returns "10.50"
 * sanitizeMoneyOnBlur("") // Returns ""
 */
export const sanitizeMoneyOnBlur = (value) => {
  if (!value || value === '') {
    return '';
  }

  const cleaned = value.toString().replace(/[^\d.]/g, '');
  const numValue = parseFloat(cleaned);

  if (isNaN(numValue)) {
    return '';
  }

  return numValue.toFixed(2);
};

// ============================================
// SIMPLE CALCULATIONS (Frontend Display Only)
// ============================================

/**
 * Add two money values (for display calculations only)
 * For backend calculations, always use backend moneyUtils
 * @param {string|number} value1 - First money value
 * @param {string|number} value2 - Second money value
 * @returns {string} Sum as string with 2 decimals
 * @example
 * addMoneyDisplay("10.50", "5.25") // Returns "15.75"
 */
export const addMoneyDisplay = (value1, value2) => {
  const num1 = parseFloat(value1) || 0;
  const num2 = parseFloat(value2) || 0;
  return (Math.round((num1 + num2) * 100) / 100).toFixed(2);
};

/**
 * Subtract two money values (for display calculations only)
 * @param {string|number} value1 - First money value
 * @param {string|number} value2 - Second money value
 * @returns {string} Difference as string with 2 decimals
 */
export const subtractMoneyDisplay = (value1, value2) => {
  const num1 = parseFloat(value1) || 0;
  const num2 = parseFloat(value2) || 0;
  return (Math.round((num1 - num2) * 100) / 100).toFixed(2);
};

/**
 * Multiply money by a number (for display calculations only)
 * @param {string|number} value - Money value
 * @param {number} multiplier - Multiplier
 * @returns {string} Product as string with 2 decimals
 */
export const multiplyMoneyDisplay = (value, multiplier) => {
  const num = parseFloat(value) || 0;
  return (Math.round(num * multiplier * 100) / 100).toFixed(2);
};

/**
 * Calculate percentage of money value (for display only)
 * @param {string|number} value - Money value
 * @param {number} percentage - Percentage (e.g., 5 for 5%)
 * @returns {string} Result as string with 2 decimals
 */
export const calculatePercentageDisplay = (value, percentage) => {
  const num = parseFloat(value) || 0;
  return (Math.round(num * (percentage / 100) * 100) / 100).toFixed(2);
};

/**
 * Sum array of money values (for display only)
 * @param {Array<string|number>} values - Array of money values
 * @returns {string} Sum as string with 2 decimals
 */
export const sumMoneyDisplay = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return '0.00';
  }

  const total = values.reduce((sum, value) => {
    const num = parseFloat(value) || 0;
    return sum + num;
  }, 0);

  return (Math.round(total * 100) / 100).toFixed(2);
};

// ============================================
// COMPARISON FUNCTIONS
// ============================================

/**
 * Compare two money values
 * @param {string|number} value1 - First money value
 * @param {string|number} value2 - Second money value
 * @returns {number} -1 if value1 < value2, 0 if equal, 1 if value1 > value2
 */
export const compareMoneyDisplay = (value1, value2) => {
  const num1 = parseFloat(value1) || 0;
  const num2 = parseFloat(value2) || 0;

  if (num1 < num2) return -1;
  if (num1 > num2) return 1;
  return 0;
};

// ============================================
// VALIDATION HELPERS
// ============================================

/**
 * Check if money value is zero
 * @param {string|number} value - Money value
 * @returns {boolean} True if zero
 */
export const isZeroMoney = (value) => {
  const num = parseFloat(value) || 0;
  return num === 0;
};

/**
 * Check if money value is positive
 * @param {string|number} value - Money value
 * @returns {boolean} True if positive
 */
export const isPositiveMoney = (value) => {
  const num = parseFloat(value) || 0;
  return num > 0;
};

/**
 * Check if money value is negative
 * @param {string|number} value - Money value
 * @returns {boolean} True if negative
 */
export const isNegativeMoney = (value) => {
  const num = parseFloat(value) || 0;
  return num < 0;
};

// ============================================
// DEFAULT EXPORT
// ============================================

export default {
  toNumberSafe,
  // Display formatting
  formatCurrency,
  formatCompactCurrency,
  formatMoneyPlain,
  formatNumberWithCommas,

  // Input parsing
  parseMoneyInput,
  isValidMoneyInput,
  formatInputValue,
  sanitizeMoneyOnBlur,

  // Display calculations
  addMoneyDisplay,
  subtractMoneyDisplay,
  multiplyMoneyDisplay,
  calculatePercentageDisplay,
  sumMoneyDisplay,

  // Comparison
  compareMoneyDisplay,

  // Validation
  isZeroMoney,
  isPositiveMoney,
  isNegativeMoney
};

